// The Opendoor face's recovery when the page chunk itself failed to load (R2-15, OD-35): the terminal-screen pattern of the kit
// (OdTerminalScreen inside OdShell: the 60 px header with the wordmark on desktop, a 500 px column, a 48 px circle icon, the H1 at 25/28
// on desktop and 20/24 on phones, a 16/24 body, the actions row with the secondary on the left and the primary on the right, full width on
// phones, and the lender strip at the foot), drawn WITHOUT the failed chunk. The failed chunk carries the kit and theme.css, so nothing
// here imports any of them: the markup is plain, its few rules are one <style> element of its own (a media query cannot live in an inline
// style), and its colors and geometry are pinned against theme.css by a spec (OpendoorLoadRecovery.test.jsx). The wordmark, the Equal
// Housing mark and the lender links are small modules with no imports. The shared Graphik stylesheet loads from the application shell
// independently of the failed chunk, so recovery uses the approved regular and medium faces.
import EqualHousing from "./assets/EqualHousing.jsx"
import OpendoorWordmark from "./assets/OpendoorWordmark.jsx"
import { LENDER_LINKS } from "./lenderLinks.js"

// The lender of every Opendoor application (D2cHelocPartnerExperience); the page's own `experience` prop, when it reaches this screen, wins.
export const RECOVERY_LENDER = Object.freeze({ lenderName: "HomeLight Home Loans, Inc.", lenderNmls: "1529229" })
export const RECOVERY_SUPPORT_PHONE = "844-882-3283"

// The theme's numbers (theme.css, [data-od] tokens), by role. The spec compares every one with the stylesheet.
export const RECOVERY_TOKENS = Object.freeze({
  ink: "#23201d",
  secondary: "#5e564e",
  tertiary: "#786e64",
  accent: "#0042e6",
  accentHover: "#00288b",
  canvas: "#ffffff",
  fill1: "#f9f8f8",
  hairline: "#dfdbd8",
  fontFamily: '"Graphik", "Graphik Fallback", Arial, sans-serif',
  weightRegular: 400,
  weightMedium: 500,
  h1: { phone: ["20px", "24px", "-0.48px"], desktop: ["25px", "28px", "-0.7px"] },
  body: ["16px", "24px", "-0.224px"],
  labelPhone: ["14px", "17.5px", "-0.196px"],
  labelDesktop: ["16px", "20px", "-0.224px"],
  legal: ["12.6px", "17.64px", "-0.1764px"],
  // The legal footer's leading (`od-t-legal-block`): the lender strip is set on the legal strip's 20.475 px, left-aligned, like the kit's.
  legalBlockLine: "20.475px",
})

const T = RECOVERY_TOKENS
const type = ([size, line, tracking]) => `font-size:${size};line-height:${line};letter-spacing:${tracking};`

// One rule per piece of the pattern, with the kit's Tailwind classes it stands for in the comments. `odr-` is this screen's own prefix.
const CSS = `
.odr{box-sizing:border-box;min-height:100vh;min-height:100dvh;display:flex;flex-direction:column;background:${T.canvas};color:${T.ink};font-family:${T.fontFamily};font-weight:${T.weightRegular};-webkit-font-smoothing:antialiased}
.odr *,.odr *::before,.odr *::after{box-sizing:border-box;font-family:inherit}
.odr-header{display:none;height:60px;width:100%;flex-shrink:0;align-items:center;justify-content:space-between;padding:0 56px}
.odr-home{display:inline-flex;align-items:center;color:${T.ink}}
.odr-main{margin:0 auto;display:flex;width:100%;max-width:500px;flex:1;flex-direction:column;padding:16px 20px 0}
.odr-slot{height:0}
.odr-icon{margin-top:24px;display:flex;width:48px;height:48px;flex-shrink:0;align-items:center;justify-content:center;border-radius:9999px;background:${T.fill1};color:${T.ink}}
.odr-title{margin:20px 0 0;color:${T.ink};font-weight:${T.weightMedium};text-wrap:balance;outline:none;${type(T.h1.phone)}}
.odr-body{margin:8px 0 0;color:${T.secondary};font-weight:${T.weightRegular};text-wrap:pretty;${type(T.body)}}
.odr-actions{margin-top:8px}
.odr-bar{display:flex;width:100%;flex-direction:column;gap:8px;margin-top:24px}
.odr-btn{display:inline-flex;align-items:center;justify-content:center;width:100%;height:44px;flex-shrink:0;gap:4px;padding:0 16px;border:1px solid transparent;border-radius:12px;cursor:pointer;text-decoration:none;font-weight:${T.weightMedium};${type(T.labelPhone)}}
.odr-btn-primary{background:${T.accent};border-color:${T.accent};color:#ffffff}
.odr-btn-primary:hover:not(:disabled){background:${T.accentHover};border-color:${T.accentHover}}
.odr-btn-primary:disabled{cursor:wait}
.odr-btn-secondary{background:${T.canvas};border-color:${T.hairline};color:${T.ink}}
.odr-btn:focus-visible{outline:1px solid ${T.ink};outline-offset:2px}
.odr-btn-primary:focus-visible{outline-color:${T.accent}}
.odr-error{margin:16px 0 0;color:${T.ink};${type(T.body)}}
.odr-foot{width:100%;max-width:500px;margin:0 auto;padding:0 20px 24px}
.odr-lender{display:flex;flex-direction:column;align-items:flex-start;gap:16px 0;text-align:left;color:${T.tertiary};font-weight:${T.weightRegular};text-wrap:pretty;${type(T.legal)}line-height:${T.legalBlockLine};}
.odr-lender p{margin:0}
.odr-mark{display:inline-block;margin-right:6px;vertical-align:-3px;color:${T.ink}}
.odr-links{display:flex;flex-wrap:wrap;align-items:center;justify-content:flex-start;gap:0 8px}
.odr-link{display:inline-flex;min-height:24px;align-items:center;color:${T.tertiary};text-decoration:underline;text-decoration-thickness:0.0625em;text-underline-offset:2px}
.odr-link:focus-visible{outline:2px solid ${T.accent};outline-offset:2px}
@media (min-width:768px){
.odr-header{display:flex}
.odr-main{padding:36px 0 0}
.odr-slot{height:4px;margin-bottom:12px}
.odr-icon{margin-top:32px}
.odr-title{${type(T.h1.desktop)}}
.odr-actions{margin-top:12px}
.odr-bar{flex-direction:row;justify-content:space-between;margin-top:0;padding:20px 0}
.odr-btn{width:auto;min-width:128px;height:48px;${type(T.labelDesktop)}}
.odr-primary{margin-left:auto}
.odr-foot{padding:0 0 32px}
.odr-links{gap:0 20px}
}
`

/** Lucide's `CircleAlert` (22 px, stroke 1.5: the kit's terminal icon), inline so this screen imports no icon library. */
function AlertIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" x2="12" y1="8" y2="12" />
      <line x1="12" x2="12.01" y1="16" y2="16" />
    </svg>
  )
}

/** `tel:` URL of a displayed phone number: digits only, with the US country code in front of a ten-digit number. */
function telHref(phone) {
  const digits = String(phone).replace(/\D/g, "")
  return `tel:${digits.length === 10 ? `1${digits}` : digits}`
}

/**
 * `busy`: the reload is running (the primary is disabled and says so). `reloadFailed`: the native reload threw (a message under the actions).
 * `experience` / `supportPhone` / `homeUrl`: the page's own props, when Inertia hands them over; the defaults are the Opendoor application's.
 */
export default function OpendoorLoadRecovery({ busy, reloadFailed, onReload, experience, supportPhone, homeUrl }) {
  const lender = experience?.lenderName ? { lenderName: experience.lenderName, lenderNmls: experience.lenderNmls } : RECOVERY_LENDER
  const phone = supportPhone || RECOVERY_SUPPORT_PHONE
  return (
    <div className="odr">
      <style>{CSS}</style>
      <header className="odr-header">
        <a className="odr-home" href={experience?.homeUrl || homeUrl || "/"} aria-label="Opendoor home">
          <OpendoorWordmark />
        </a>
      </header>
      <main className="odr-main">
        <div className="odr-slot" />
        <span className="odr-icon" aria-hidden="true">
          <AlertIcon />
        </span>
        <h1 className="odr-title">We couldn’t load your application</h1>
        <p className="odr-body" role="status" aria-live="polite">
          Reload to try again. Your application link will be kept.
        </p>
        <div className="odr-actions">
          <div className="odr-bar">
            <a className="odr-btn odr-btn-secondary" href={telHref(phone)}>
              Call {phone}
            </a>
            <button type="button" className="odr-btn odr-btn-primary odr-primary" disabled={busy} aria-busy={busy} onClick={onReload}>
              {busy ? "Reopening application..." : "Reload application"}
            </button>
          </div>
        </div>
        {reloadFailed ? (
          <p className="odr-error" role="alert">
            Please try reloading again.
          </p>
        ) : null}
      </main>
      <div className="odr-foot">
        <footer className="odr-lender">
          <p>
            <EqualHousing className="odr-mark" size={16} />
            {`${lender.lenderName} NMLS ${lender.lenderNmls}`}
            {" · Equal Housing Opportunity"}
          </p>
          <nav className="odr-links" aria-label="Legal">
            {LENDER_LINKS.map((link) => (
              <a key={link.label} className="odr-link" href={link.href} target="_blank" rel="noopener noreferrer">
                {link.label}
              </a>
            ))}
          </nav>
        </footer>
      </div>
    </div>
  )
}
